import Reveal from "../components/Reveal";

interface Metric { v: string; l: string; }
interface Case { tag: string; title: string; quote: string; metrics: Metric[]; }

const CASES: Case[] = [
  {
    tag: "Sample · Mining",
    title: "AI agents and MCP-connected monitoring co-workers for a mining operation",
    quote: "For mining operators we can build AI agents connected to live operational data via MCP, surfacing load-haul inefficiencies, flagging problems before they become losses and delivering full transparency across the operation. Continuous automated oversight replaces hours of manual monitoring, and the savings generated in the first engagement period typically cover the entire project cost.",
    metrics: [
      { v: "-18%",  l: "Cycle time" },
      { v: "+12%",  l: "Truck utilisation" },
      { v: "6 wks", l: "To production" },
    ],
  },
  {
    tag: "Sample · Machinery",
    title: "AI agents for parts management, field tracking and condition-based maintenance across 400+ assets",
    quote: "For machinery fleets we can build AI agents that monitor every asset for fault conditions, source and order the right parts automatically, locate machines in the yard or on site in real time, and deliver manufacturer-guided repair instructions directly to the technician. The result is faster hire turnaround, live fleet visibility for sales, and significantly reduced unplanned downtime, with maintenance spend following.",
    metrics: [
      { v: "-34%",  l: "Unplanned downtime" },
      { v: "×2.1",  l: "Asset life extension" },
      { v: "8 wks", l: "Pilot to scale" },
    ],
  },
  {
    tag: "Sample · GIS",
    title: "AI agents for multi-source geospatial analysis, data fusion and decision-ready reporting",
    quote: "For geospatial and government teams we can build agents that ingest any GIS format, structured or unstructured, cross-reference company and open datasets, surface patterns and anomalies invisible to human analysis, and generate polished decision-ready reports in seconds. Every finding is visualised, every action is backed by evidence, and the time from data to decision drops from days to minutes.",
    metrics: [
      { v: "7×",     l: "Analyst throughput" },
      { v: "99.2%",  l: "Precision @ recall 0.9" },
      { v: "12 wks", l: "Discovery to live" },
    ],
  },
  {
    tag: "Sample · Defence-Ready",
    title: "AUKUS-ready compliance, policy generation and staff training for a defence supply-chain entrant",
    quote: "For organisations entering defence and government supply chains we can audit existing policies, technical setup and access controls against Essential Eight, ISO 27001, NIST CSF and AUKUS export control requirements. We identify gaps, generate and fix policies, train staff against government standards, and assemble the full evidence pack for procurement submission. The engagement delivers a defensible AUKUS-ready status, opening the door to defence contracts and government programmes.",
    metrics: [
      { v: "Full",   l: "Audit completed" },
      { v: "100+",   l: "Policies generated" },
      { v: "AUKUS",  l: "Ready status" },
    ],
  },
];

export default function Cases() {
  return (
    <section id="cases" className="bg-black py-28 md:py-40 px-6 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <div className="flex items-end justify-between gap-6 flex-wrap mb-12 md:mb-16">
            <div>
              <div className="text-white/40 text-sm tracking-widest uppercase">Outcomes</div>
              <h2 className="font-instrument text-5xl md:text-7xl text-white tracking-tight mt-4 leading-[1.05]">
                Sample <em className="italic-serif text-white/50">case studies</em>
              </h2>
            </div>
            <a href="#contact" className="liquid-glass rounded-full px-6 py-2.5 text-white text-sm font-medium hover:bg-white/5 transition-colors">
              Request NDA case pack
            </a>
          </div>
        </Reveal>

        <div className="flex flex-col gap-5">
          {CASES.map((c, i) => (
            <Reveal key={c.title} delay={i * 120}>
              <article className="liquid-glass rounded-3xl p-6 md:p-10 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
                <div>
                  <div className="text-white/40 text-xs tracking-widest uppercase">{c.tag}</div>
                  <h3 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-3">{c.title}</h3>
                  <p className="text-white/60 text-base leading-relaxed mt-4 max-w-2xl">{c.quote}</p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {c.metrics.map((m) => (
                    <div key={m.l} className="liquid-glass rounded-2xl p-4 text-center">
                      <div className="font-instrument text-2xl md:text-3xl text-white">{m.v}</div>
                      <div className="text-white/40 text-[10px] tracking-widest uppercase mt-1 leading-tight">{m.l}</div>
                    </div>
                  ))}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
